import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Music, ShoppingBag, Mail, ShieldCheck, Loader2, Lock, ArrowUpRight, Play, Pause } from "lucide-react";
import { api, formatApiError, imgUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import SectionLabel from "@/components/SectionLabel";

/**
 * Exclusive single-track downloads sold for $1.99 (or whatever price is set in admin).
 *
 * Props:
 *  - variant: 'full' (renders all tracks as a grid — used on /shop)
 *           | 'teaser' (renders compact card linking to /shop#exclusive-downloads — used on /music)
 *  - limit: max number of releases to render (default 6 for full, 1 for teaser)
 */
export default function ExclusiveDownloads({ variant = "full", limit = 6 }) {
  const [releases, setReleases] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [playingSlug, setPlayingSlug] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    api
      .get("/public/digital-releases")
      .then((r) => setReleases(r.data?.releases || []))
      .catch(() => setReleases([]))
      .finally(() => setLoaded(true));
  }, []);

  const togglePreview = (release) => {
    const el = audioRef.current;
    if (!el) return;
    if (playingSlug === release.slug) {
      el.pause();
      return;
    }
    el.src = imgUrl(release.preview_url);
    el.currentTime = 0;
    el.play().then(() => setPlayingSlug(release.slug)).catch(() => setPlayingSlug(null));
  };

  if (loaded && releases.length === 0) return null;

  if (variant === "teaser") {
    const featured = releases[0];
    return (
      <section
        id="exclusive-downloads-teaser"
        className="py-16 border-t border-white/5"
        data-testid="downloads-teaser-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Link
            to="/shop#exclusive-downloads"
            className="group surface relative flex flex-col lg:flex-row items-stretch gap-0 overflow-hidden hover:border-[color:var(--gold)]/40 transition-all duration-300"
            data-testid="downloads-teaser-card"
          >
            <div
              className="lg:w-[34%] aspect-[4/3] lg:aspect-auto relative overflow-hidden"
              style={{
                background:
                  "radial-gradient(ellipse 90% 90% at 30% 30%, rgba(232,180,184,0.22), transparent 70%), #1a0d12",
              }}
            >
              {featured?.cover_image ? (
                <img
                  src={imgUrl(featured.cover_image)}
                  alt={featured.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Lock size={42} style={{ color: "var(--gold)" }} strokeWidth={1} />
                </div>
              )}
              <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.25em] px-2.5 py-1 bg-black/70 backdrop-blur-sm border border-[color:var(--gold)]/30 rounded-full text-[color:var(--gold)]">
                <Lock size={9} />
                Site-Only Exclusive
              </span>
            </div>
            <div className="flex-1 p-8 lg:p-10 flex flex-col justify-center space-y-3">
              <SectionLabel label="Exclusive Downloads · $1.99" />
              <h3 className="font-serif text-3xl lg:text-4xl tracking-tighter font-light">
                Singles you{" "}
                <em className="italic" style={{ color: "var(--gold)" }}>
                  can&rsquo;t stream
                </em>{" "}
                anywhere.
              </h3>
              <p className="text-sm text-white/55 leading-relaxed max-w-md">
                Off-platform drops, sold only here. Lossy MP3 download, no DRM, yours to keep —
                straight from Nina to your library.
              </p>
              <span
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[color:var(--gold)] mt-2"
                data-testid="downloads-teaser-cta"
              >
                Browse the Shop
                <ArrowUpRight
                  size={12}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </span>
            </div>
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section
      id="exclusive-downloads"
      className="py-20 border-t border-white/5 scroll-mt-24"
      data-testid="downloads-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 space-y-10">
        <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <SectionLabel label="Site-Only Exclusives" />
            <h2 className="font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mt-3">
              Downloads you{" "}
              <em className="italic" style={{ color: "var(--gold)" }}>
                won&rsquo;t find
              </em>{" "}
              on streaming.
            </h2>
            <p className="text-white/65 leading-relaxed mt-4">
              Singles dropped only for ninacapone.com visitors. $1.99 each — high-quality MP3,
              yours to keep. Link valid 7 days · 5 downloads.
            </p>
          </div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-white/45 inline-flex items-center gap-1.5 shrink-0">
            <ShieldCheck size={12} style={{ color: "var(--gold)" }} />
            Stripe-secured · Auto-emailed
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="downloads-grid">
          {releases.slice(0, limit).map((r) => (
            <DownloadCard
              key={r.id}
              release={r}
              isPlaying={playingSlug === r.slug}
              onTogglePreview={() => togglePreview(r)}
            />
          ))}
        </div>
      </div>

      {/* Shared hidden audio element for previews — keeps state in sync across all cards */}
      <audio
        ref={audioRef}
        onPlay={() => {/* state set by togglePreview */}}
        onPause={() => setPlayingSlug(null)}
        onEnded={() => setPlayingSlug(null)}
        preload="none"
        data-testid="downloads-preview-audio"
      />
    </section>
  );
}

function DownloadCard({ release, isPlaying, onTogglePreview }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const emailRef = useRef(null);

  const openForm = () => {
    setShowForm(true);
    setTimeout(() => emailRef.current?.focus(), 100);
  };

  const buy = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Email required so we can deliver the track.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/public/digital-releases/${release.slug}/checkout`,
        {
          email,
          name: name || undefined,
          origin_url: window.location.origin,
        }
      );
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't start checkout.");
      setBusy(false);
    }
  };

  const priceLabel = `$${((release.price_cents || 199) / 100).toFixed(2)}`;

  return (
    <article
      className="group surface relative overflow-hidden flex flex-col"
      data-testid={`download-card-${release.slug}`}
    >
      <div
        className="relative aspect-square overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse 90% 90% at 30% 30%, rgba(232,180,184,0.18), transparent 70%), #1a0d12",
        }}
      >
        {release.cover_image ? (
          <img
            src={imgUrl(release.cover_image)}
            alt={release.title}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
            data-testid={`download-cover-${release.slug}`}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Music size={56} style={{ color: "var(--gold)" }} strokeWidth={1} />
          </div>
        )}

        {/* Preview play button — only renders when a preview is available */}
        {release.preview_url && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onTogglePreview) onTogglePreview();
            }}
            aria-label={isPlaying ? "Pause preview" : "Play 30-second preview"}
            className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
              isPlaying
                ? "bg-black/45 opacity-100"
                : "bg-black/0 hover:bg-black/55 opacity-0 group-hover:opacity-100 focus:opacity-100"
            }`}
            data-testid={`download-preview-${release.slug}`}
          >
            <span
              className="flex items-center justify-center w-16 h-16 rounded-full transition-transform group-hover:scale-110"
              style={{
                background: "var(--gold)",
                color: "#120a0d",
                boxShadow: "0 8px 32px rgba(232,180,184,0.4)",
              }}
            >
              {isPlaying ? <Pause size={22} fill="#120a0d" /> : <Play size={22} fill="#120a0d" className="ml-1" />}
            </span>
            <span
              className="absolute bottom-3 right-3 text-[9px] uppercase tracking-[0.25em] px-2 py-1 bg-black/65 backdrop-blur-sm border border-white/15 rounded-full text-white/85"
            >
              {isPlaying ? "Playing · 30s" : "Preview · 30s"}
            </span>
          </button>
        )}

        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.25em] px-2 py-1 bg-black/65 backdrop-blur-sm border border-[color:var(--gold)]/30 text-[color:var(--gold)] rounded-full pointer-events-none">
          <Lock size={9} />
          Exclusive
        </span>
        <span className="absolute top-3 right-3 inline-flex items-center text-[10px] uppercase tracking-[0.25em] px-2.5 py-1 bg-[color:var(--gold)] text-[color:var(--bg)] rounded-full font-semibold pointer-events-none">
          {priceLabel}
        </span>
      </div>

      <div className="p-6 flex-1 flex flex-col gap-4">
        <div>
          <h3
            className="font-serif text-2xl tracking-tighter font-light leading-tight group-hover:text-[color:var(--gold)] transition-colors"
            data-testid={`download-title-${release.slug}`}
          >
            {release.title}
          </h3>
          <p className="text-[11px] uppercase tracking-[0.22em] text-white/50 mt-1">
            {release.artist} · MP3
          </p>
        </div>
        {release.description && (
          <p className="text-sm text-white/55 leading-relaxed line-clamp-3">{release.description}</p>
        )}

        <div className="mt-auto pt-2">
          {!showForm ? (
            <button
              type="button"
              onClick={openForm}
              className="btn-gold inline-flex items-center gap-2 w-full justify-center px-5 py-3 text-xs"
              data-testid={`download-buy-${release.slug}`}
            >
              Buy & Download · {priceLabel}
              <Lock size={12} />
            </button>
          ) : (
            <form onSubmit={buy} className="space-y-3" data-testid={`download-form-${release.slug}`}>
              <div>
                <Label htmlFor={`dl-name-${release.slug}`} className="eyebrow">
                  Name (optional)
                </Label>
                <Input
                  id={`dl-name-${release.slug}`}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 h-10"
                  data-testid={`download-name-${release.slug}`}
                />
              </div>
              <div>
                <Label htmlFor={`dl-email-${release.slug}`} className="eyebrow">
                  Email *
                </Label>
                <Input
                  id={`dl-email-${release.slug}`}
                  type="email"
                  required
                  ref={emailRef}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 h-10"
                  data-testid={`download-email-${release.slug}`}
                />
              </div>
              <Button
                type="submit"
                disabled={busy || !email}
                className="btn-gold w-full justify-center gap-2 py-3 text-xs"
                data-testid={`download-submit-${release.slug}`}
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <ShoppingBag size={14} />}
                {busy ? "Redirecting…" : `Continue · ${priceLabel}`}
              </Button>
              <p className="text-[10px] uppercase tracking-[0.22em] text-white/35 leading-relaxed flex items-start gap-1.5">
                <Mail size={10} className="mt-0.5 shrink-0" />
                We email the download link after payment · 7 days · 5 downloads
              </p>
            </form>
          )}
        </div>
      </div>
    </article>
  );
}
